import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import { forkJoin } from 'rxjs/internal/observable/forkJoin';
import {
  NbToastrService,
  NbDialogService,
  NbLayoutScrollService,
} from '@nebular/theme';
import { JoyrideService } from 'ngx-joyride';
import {
  SearchCountryField,
  CountryISO,
  PhoneNumberFormat,
} from 'ngx-intl-tel-input';

import {
  PatientData,
  Patient,
} from '../../../@core/interfaces/common/CalorieFriend/patients';
import {
  EMAIL_PATTERN,
  PHONE_PATTERN,
} from '../../../@auth/components/constants';
import { Goal } from '../../../@core/interfaces/common/CalorieFriend/goals';
import { Utility } from '../../../@core/utils/utility';
import {
  Country,
  CountryData,
} from '../../../@core/interfaces/common/countries';
import {
  TutorialData,
  TutorialIDs,
} from 'app/@core/interfaces/common/CalorieFriend/tutorial';
import { SubscriptionPlanData } from 'app/@core/interfaces/common/CalorieFriend/subscriptionPlan';
import { UserData } from 'app/@core/interfaces/common/users';
import { TempGoalListComponent } from '../Goals/TempGoalList/temp-goal-list.component';
import { BaseComponentCanDeactivate } from 'app/@auth/guards/changes.guard';
import { UpgradePlanComponent } from '../Patients/UpgradePlan/upgrade-plan.component';
import { PatientSubscriptionsMetaService } from 'app/@core/backend/common/services/CalorieFriend/patient-subscriptions-meta.service';
import { GridData } from 'app/@core/interfaces/common/grid-data';
import { PatientSubscriptionMeta } from 'app/@core/interfaces/common/CalorieFriend/patient-subscription';

/**
 * Component for creation/edition of the Patient
 */
@Component({
  selector: 'app-add-patient',
  templateUrl: './patient.component.html',
  styleUrls: ['./patient.component.scss', '../global.component.scss'],
})
export class PatientComponent
  extends BaseComponentCanDeactivate
  implements OnInit {
  PatientForm: FormGroup;

  AddPaitentLabel = 'Add New Client';
  UpdatePaitentLabel = 'Update Client';
  PaitentLabel = '';

  SavePatientLabel = 'Save Client';
  GeneralInfoLabel = 'General Info';
  FirstNameLabel = 'First Name';
  NameLabel = 'Name';
  LastNameLabel = 'Last Name';
  EmailLabel = 'Email Address';
  BirthDateLabel = 'Date of Birth';
  AddressInfoLabel = 'Address';
  AddressLine1Label = 'Line 1';
  AddressLine2Label = 'Line 2';
  CountryInfoLabel = 'Country';
  PostalCodeInfoLabel = 'Zip / Postal Code';
  PhoneInfoLabel = 'Phone Number';
  BiometricInfoLabel = 'BioMetric Info';
  PreferencesLabel = 'Preferences';
  PreferredUnitLabel = 'Preferred Units';
  HeightLabel = 'Height';
  WeightLabel = 'Weight';
  BodyFatLabel = 'Body Fat %';
  SourceLabel = 'Source';
  BMILabel = 'BMI';
  MeasureDateLabel = 'Date of Measure';
  BackLabel = 'Back';
  Created = 'Created';
  Updated = 'Updated';
  ProblemCeatingPatient = 'A problem occurred creating the patient.';
  PlansLabel = 'Plans';
  GoalsLabel = 'Goals';

  GoalDisable = '1';
  PatientId: number = null;
  isTutorial: boolean = false;

  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  preferredCountries: CountryISO[] = [
    CountryISO.UnitedStates,
    CountryISO.Canada,
  ];

  protected readonly unsubscribe$ = new Subject<void>();

  Countries: Observable<Country[]>;

  tempGoals: Array<Goal> = new Array();

  @ViewChild('Goals') private GoalsComponent: TempGoalListComponent = null;

  public subscriptionData: GridData<PatientSubscriptionMeta>;

  /** Patient ctor */
  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private patientsService: PatientData,
    private toasterService: NbToastrService,
    private countriesService: CountryData,
    private dialogService: NbDialogService,
    private subscriptionPlansService: SubscriptionPlanData,
    private usersService: UserData,
    public readonly joyrideService: JoyrideService,
    private readonly tutorialService: TutorialData,
    private patientSubscriptionsMetaService: PatientSubscriptionsMetaService,
    private layout: NbLayoutScrollService
  ) {
    super(dialogService);
  }

  get Id() {
    return this.PatientForm.get('Id');
  }
  get FirstName() {
    return this.PatientForm.get('FirstName');
  }
  get LastName() {
    return this.PatientForm.get('LastName');
  }
  get EMail() {
    return this.PatientForm.get('EMail');
  }
  get DateOfBirth() {
    return this.PatientForm.get('DateOfBirth');
  }
  get AddressLine1() {
    return this.PatientForm.get('AddressLine1');
  }
  get AddressLine2() {
    return this.PatientForm.get('AddressLine2');
  }
  get Country() {
    return this.PatientForm.get('Country');
  }
  get ZipPostalCode() {
    return this.PatientForm.get('ZipPostalCode');
  }
  get PhoneNumber() {
    return this.PatientForm.get('PhoneNumber');
  }
  get Height() {
    return this.PatientForm.get('Height');
  }
  get Weight() {
    return this.PatientForm.get('Weight');
  }
  get BodyFat() {
    return this.PatientForm.get('BodyFat');
  }
  get Source() {
    return this.PatientForm.get('Source');
  }
  get DateOfMeasure() {
    return this.PatientForm.get('DateOfMeasure');
  }
  get IsPreferredUnitImperial() {
    return this.PatientForm.get('IsPreferredUnitImperial');
  }
  get genderFemale() {
    return this.PatientForm.get('genderFemale');
  }
  get BMI() {
    if (!this.Height.value || this.Height.value === 0) return 'N/A';
    return (
      (this.Weight.value / (this.Height.value * this.Height.value)) *
      10000
    ).toFixed(1);
  }

  get SubscriptionId() {
    return this.PatientForm.get('SubscriptionId');
  }

  /**
   * Initializes the component.
   * It creates the patient Form and loads the patient data if it is for edition of an existing patient.
   */
  ngOnInit(): void {
    const loadUser = this.usersService.getCurrentUser();
    this.patientSubscriptionsMetaService.list({
      showCanceled: false,
      isDeleted: false
    }).subscribe((data: GridData<PatientSubscriptionMeta>) => {
      this.subscriptionData = data;
    });

    forkJoin([loadUser, this.patientsService.getActiveCount()]).subscribe(
      (data) => {
        const user = data[0];
        const count = data[1];

        const loadCurrentSubPlan = this.subscriptionPlansService.getByCode(
          user.planCode
        );
        loadCurrentSubPlan.subscribe((plan) => {
          const curSubPlan = plan;

          if (
            curSubPlan.patientsCount !== -1 &&
            count >= curSubPlan.patientsCount
          ) {
            this.dialogService
              .open(UpgradePlanComponent, {
                closeOnEsc: false,
                closeOnBackdropClick: false,
              })
              .onClose.subscribe((result) => {
                if (result === 'cancel') {
                  this.router.navigate(['/pages/CalorieFriend/Patients/']);
                }
              });
          }
        });

        this.isTutorial =
          this.route.snapshot.url.length > 1
            ? this.route.snapshot.url[1].toString().toLowerCase() ===
            'fromtutorial'
            : false;

        const tutorialStates = JSON.parse(user.tutorialStates ?? '{}');

        // check the tutorial step 1 - demo patient
        if (!tutorialStates[TutorialIDs.NewPatient] && this.isTutorial) {
          // show tutorial
          setTimeout(() => {
            const steps = [];

            // add two empty steps
            //steps.push('');
            //steps.push('');

            // pre-fill data
            this.FirstName.setValue(user.firstName);
            this.LastName.setValue(user.lastName);

            this.EMail.setValue(user.email);
            this.PhoneNumber.setValue(user.phoneNumber);

            this.Height.setValue('180');
            this.Weight.setValue('75');

            steps.push(TutorialIDs.NewPatientStep1);
            steps.push('');
            // add fake steps to make correct step numbers
            //for (let i = 0; i < 4; i++) steps.push('');

            this.joyrideService
              .startTour(
                {
                  steps: steps,
                  showPrevButton: false,
                  showCounter: true,
                  customTexts: {
                    next: 'Okay',
                  },
                } // Your steps order
              )
              .subscribe();
          }, 1000);
        }
      }
    );

    this.InitUserForm();

    const id = this.route.snapshot.paramMap.get('id');

    if (id !== null) {
      this.loadPatientData(id);
      this.PaitentLabel = this.UpdatePaitentLabel;
      this.GoalDisable = '0';
    } else {
      this.PaitentLabel = this.AddPaitentLabel;
      this.GoalDisable = '0';
    }

    // load countries
    this.Countries = this.countriesService.list();
  }

  @ViewChild('saveElement') saveElement: any;
  @ViewChild('topElement') topElement: any;

  onTutorialNext1() {
    this.joyrideService.closeTour();
    this.SavePatient();
    return;

    setTimeout(() => {
      this.saveElement.hostElement.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      // show tutorial
      setTimeout(() => {
        const steps = [];

        // add two empty steps
        //steps.push('');
        //steps.push('');

        steps.push(TutorialIDs.NewPatientStep2);

        // add fake steps to make correct step numbers
        //for (let i = 0; i < 4; i++) steps.push('');

        this.joyrideService
          .startTour(
            {
              steps: steps,
              showPrevButton: false,
              showCounter: true,
            } // Your steps order
          )
          .subscribe(
            (step) => {
              /*Do something*/
            },
            (error) => {
              /*handle error*/
            },
            () => {
              /*Tour is finished here, do something*/
            }
          );
      }, 1000);
    }, 1000);
  }

  onTutorialNext2() {
    this.SavePatient();
  }

  onTutorialDone() {
    this.tutorialService.save(TutorialIDs.NewPatient).subscribe(() => { });
  }

  /**
   * Initializes the patient Form
   */
  InitUserForm() {
    this.PatientForm = this.fb.group({
      Id: this.fb.control(''),
      FirstName: this.fb.control('', [
        Validators.required,
        Validators.minLength(0),
        Validators.maxLength(50),
      ]),
      LastName: this.fb.control('', [
        Validators.required,
        Validators.minLength(0),
        Validators.maxLength(50),
      ]),
      EMail: this.fb.control('', [
        Validators.required,
        Validators.pattern(EMAIL_PATTERN),
      ]),
      DateOfBirth: this.fb.control(new Date(new Date().setHours(0, 0, 0, 0)), [
        Validators.required,
      ]),
      AddressLine1: this.fb.control('', [
        Validators.minLength(0),
        Validators.maxLength(1024),
      ]),
      AddressLine2: this.fb.control('', [
        Validators.minLength(0),
        Validators.maxLength(1024),
      ]),
      Country: this.fb.control('', [
        Validators.minLength(0),
        Validators.maxLength(1024),
      ]),
      ZipPostalCode: this.fb.control('', [
        Validators.minLength(0),
        Validators.maxLength(25),
      ]),
      PhoneNumber: this.fb.control(''),
      Height: this.fb.control('', [
        Validators.minLength(0),
        Validators.maxLength(15),
      ]),
      Weight: this.fb.control('', [
        Validators.minLength(0),
        Validators.maxLength(15),
      ]),
      Source: this.fb.control('Clinical', [
        Validators.required,
        Validators.minLength(0),
        Validators.maxLength(25),
      ]),
      BodyFat: this.fb.control('', [
        Validators.min(0),
        Validators.max(100),
        Validators.pattern('^-?[0-9]*.?[0-9]{0,1}$'),
      ]),
      DateOfMeasure: this.fb.control(
        new Date(new Date().setHours(0, 0, 0, 0)),
        [Validators.required]
      ),
      IsPreferredUnitImperial: this.fb.control(true),
      genderFemale: this.fb.control(false),
      SubscriptionId: this.fb.control(0),
      checkInRequest: this.fb.control(true),
    });

    super.initForm(this.PatientForm);
  }

  /**
   * Converts the object given as a parameter to the 'Patient' type
   * @param value Object to convert into 'Patient' type
   */
  ConvertToPatient(value: any): Patient {
    const patient: Patient = {
      ...value,
      PhoneNumber: (value.PhoneNumber ? value.PhoneNumber.internationalNumber : ''),
    };
    return patient;
  }

  /**
   * It converts the inputs of the form into the Patient type
   * and submit the form to save them
   */
  SavePatient() {
    const patient: Patient = this.ConvertToPatient(this.PatientForm.value);

    patient.id = this.PatientId;
    patient.updateBiometrics = true;
    patient.updateGeneralInfo = true;
    patient.goalDefs = this.tempGoals;

    if (patient.id === null) {
      patient.fitnessProgramMode = -1;
      patient.mealProgramMode = -1;

      if (this.isTutorial) {
        patient.fitnessProgramMode = 1;
      }
    }

    const observable =
      patient.id === null
        ? this.patientsService.create(patient)
        : this.patientsService.update(patient, null);

    this.resetDirty();

    observable.pipe(takeUntil(this.unsubscribe$)).subscribe(
      (data) => {
        // go to patients list or patient page
        if (this.isTutorial) {
          // save the step
          this.tutorialService.save(TutorialIDs.NewPatient).subscribe(() => { });

          // close tutorial
          //this.joyrideService.closeTour();

          // go to patient page (use full refresh)
          //this.layout.scrollTo(0,0);

          //fix for iOS bug.
          //window.location.href = `/pages/CalorieFriend/PatientSheetView/${data.id}/FromTutorial`;
          this.router.navigate([
            `/pages/CalorieFriend/PatientSheetView/${data.id}/FromTutorial`,
          ]);
        } else {
          this.patientsService.setPatientUpdated(data.id);
          this.handleSuccessResponse();
          this.router.navigate([
            `/pages/CalorieFriend/PatientSheetView/${data.id}`,
          ]);
        }
      },
      (err) => {
        this.handleWrongResponse();
      }
    );
  }

  /**
   * Navigates to the Patients list page
   */
  Back() {
    this.router.navigate(['/pages/CalorieFriend/Patients']);
  }

  /**
   * Handler of Success event of submit
   */
  handleSuccessResponse() {
    if (this.PatientId === null)
      this.toasterService.success('', `New Client Created!`);
    else this.toasterService.success('', `Client Updated!`);
  }

  /**
   * Handler of Fail event of submit
   */
  handleWrongResponse() {
    this.toasterService.danger('', this.ProblemCeatingPatient);
  }

  /**
   * Loads the data of the Patient given by {id}
   * @param id The identifier of the Patient
   */
  loadPatientData(id) {
    if (id) {
      this.patientsService
        .get(id, null)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((patient) => {
          this.PatientId = patient.id;

          const measureDateUtc = new Date(patient.dateOfMeasure);
          const measureDateLocal = new Date(
            measureDateUtc.getTime() -
            measureDateUtc.getTimezoneOffset() * 60000
          );

          const birthDateUtc = new Date(patient.dateOfBirth);
          const birthDateLocal = new Date(
            birthDateUtc.getTime() - birthDateUtc.getTimezoneOffset() * 60000
          );

          this.PatientForm.patchValue({
            Id: patient.id,
            FirstName: patient.firstName,
            LastName: patient.lastName,
            EMail: patient.eMail,
            DateOfBirth: birthDateLocal,
            AddressLine1: patient.addressLine1,
            AddressLine2: patient.addressLine2,
            Country: patient.country,
            ZipPostalCode: patient.zipPostalCode,
            PhoneNumber: patient.phoneNumber,
            Height: patient.height,
            Weight: patient.weight,
            BodyFat: patient.bodyFat,
            DateOfMeasure: measureDateLocal,
            IsPreferredUnitImperial: patient.isPreferredUnitImperial,
            genderFemale: patient.genderFemale,
          });

          super.resetDirty();
        });
    }
  }

  /**
   * Navigates to the page of Goals list of the patient
   */
  Goals() {
    if (this.PatientId > 0) {
      this.router.navigate(['/pages/CalorieFriend/Goals/' + this.PatientId]);
    }
  }

  /**
   * Navigates to the page of the Plans list of the patient
   */
  Plans() {
    this.router.navigate(['/pages/CalorieFriend/PlanList/' + this.PatientId]);
  }

  onChangePreferredUnit() {
    this.GoalsComponent.RefreshPlans();
  }

  /**
   * Validator that validates the number with the ##.# pattern
   */
  validateNumber = Utility.validateNumber;

  TutorialIDs = TutorialIDs;
}
